import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useIssueVerification, useSession } from '../../api/hooks.js';
import { Button } from '../../components/Button.js';
import { CheckboxRow, Field, Select, TextInput } from '../../components/Field.js';
import { MONO } from '../../components/classes.js';
import { Note, ProblemNote } from '../../components/Notes.js';
import { SidePanel } from '../../components/SidePanel.js';
import { CHECK_LABELS, PURPOSE_LABELS, titleCase } from '../../lib/format.js';
import type { CheckName, EntityProfile, Purpose } from '../../lib/types.js';

const DEFAULT_CHECKS: Record<CheckName, boolean> = {
  registry: true,
  tax: true,
  vat: true,
  returns: true,
  licences: false,
  screening: false,
};

const CHECK_NOTES: Partial<Record<CheckName, string>> = {
  licences: '— sector registers linked to this entity',
  screening: '— sanctions, debarment and Gazette notices',
};

/**
 * Wireframe §6 / Issue artboard: choose the checks, say why, add a
 * reference, see the quota at the moment of spend — then issue. Both
 * registers are re-read live by the server unless read in the last 15 min.
 */
export function IssuePanel({
  entity,
  open,
  onClose,
}: {
  entity: EntityProfile;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const session = useSession();
  const issue = useIssueVerification();
  const [checks, setChecks] = useState<Record<CheckName, boolean>>(DEFAULT_CHECKS);
  const [purpose, setPurpose] = useState<Purpose>('supplier_onboarding');
  const [reference, setReference] = useState('');

  const chosen = (Object.keys(checks) as CheckName[]).filter((k) => checks[k]);
  const ent = session.data?.entitlements;
  const quotaLine =
    ent && ent.remaining !== null ? (
      <>
        Uses <span className="font-semibold text-ink">1 of {ent.remaining} records</span>{' '}
        left {ent.quota_window === 'day' ? 'today' : 'this month'}.
      </>
    ) : (
      <>No record limit on your plan.</>
    );

  const submit = () => {
    if (chosen.length === 0) return;
    issue.mutate(
      {
        entity_id: entity.id,
        checks: chosen,
        purpose,
        ...(reference.trim() ? { reference: reference.trim().slice(0, 64) } : {}),
      },
      {
        onSuccess: (env) =>
          navigate(`/records/${env.data.id}`, { state: { issued: true } }),
      },
    );
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      labelledBy="issue-title"
      title={
        <>
          <span className="text-[13px] text-mute">Verification record</span>
          <span id="issue-title" className="text-[22px] font-semibold tracking-[-0.01em]">
            {titleCase(entity.name)}
          </span>
          <span className={`${MONO} text-[12px] text-mute`}>
            {entity.dira_id ?? entity.id}
          </span>
        </>
      }
      footer={
        <>
          <Button
            variant="primary"
            onClick={submit}
            disabled={issue.isPending || chosen.length === 0}
          >
            {issue.isPending ? 'Reading the registers…' : 'Issue record'}
          </Button>
          <Button onClick={onClose} disabled={issue.isPending}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col">
        <div className="mb-1 text-[13px] font-semibold tracking-[0.02em] text-mute">
          Checks to include
        </div>
        {(Object.keys(DEFAULT_CHECKS) as CheckName[]).map((name, i) => (
          <CheckboxRow
            key={name}
            id={`check-${name}`}
            first={i === 0}
            checked={checks[name]}
            onChange={(v) => setChecks((c) => ({ ...c, [name]: v }))}
            label={CHECK_LABELS[name]}
            note={CHECK_NOTES[name]}
          />
        ))}
      </div>
      <Field id="purpose" label="Purpose (kept with the record)">
        <Select
          id="purpose"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value as Purpose)}
        >
          {(Object.keys(PURPOSE_LABELS) as Purpose[]).map((p) => (
            <option key={p} value={p}>
              {PURPOSE_LABELS[p]}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="reference" label="Your reference (optional)">
        <TextInput
          id="reference"
          type="text"
          maxLength={64}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="e.g. vendor no. 4471"
        />
      </Field>
      <Note>
        Both registers are re-read at the moment of issue; the record carries those read
        times. {quotaLine}
      </Note>
      {issue.isError ? (
        <ProblemNote
          error={issue.error}
          action={
            <button
              type="button"
              onClick={submit}
              className="cursor-pointer font-semibold text-accent"
            >
              Retry now
            </button>
          }
        />
      ) : null}
    </SidePanel>
  );
}
